import { merge } from "lodash";
import { createContext, ReactNode, useContext } from "react";
import { NavigateOptions, To, useNavigate } from "react-router-dom-v5-compat";

import { useLocationOnMount } from "seneca-common/utils/hooks/routing";

type LocationContextType = {
  navigateInContext: (to: To, options?: NavigateOptions) => void;
};

const LocationContext = createContext<LocationContextType | null>(null);

function useNavigateInContext() {
  const navigate = useNavigate();
  const { search, state } = useLocationOnMount();

  return (to: To, options?: NavigateOptions) =>
    navigate(
      typeof to === "string"
        ? { pathname: to, search }
        : { ...to, search: merge(search, to.search) },
      { ...options, state: merge(state, options?.state) }
    );
}

export default function LocationProvider({
  children
}: {
  children: ReactNode;
}) {
  const navigateInContext = useNavigateInContext();

  return (
    <LocationContext.Provider value={{ navigateInContext }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);
  if (context === null) {
    throw new Error(
      "useLocationContext must be used within a LocationProvider"
    );
  }
  return context;
}
